import { Modal } from "@mui/material";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  CombinedReturnItem,
  CompensationMethodData,
  InputAnswerPartWithStep,
  isEnsoEngraving,
  isVariantInStock,
  ItemReturnFlowStep,
  NewReturnItemData,
  StepType,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import {
  ExchangeVariant,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import {
  getMultipleChoiceAnswers,
  getNonPrimaryProducts,
  getPrimaryProduct,
  walkFlow,
} from "@redotech/redo-model/draft-return/util";
import {
  OfferingSelection,
  widgetConfigToText,
} from "@redotech/redo-model/extended-warranty";
import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import { Channel } from "@redotech/redo-model/return";
import {
  FieldId,
  InputAnswerPart,
  InputPartType,
  MultipleChoiceOption,
  returnFlowJsonFormat,
  Step,
} from "@redotech/redo-model/return-flow";
import { THIRD_PARTY_SOURCE } from "@redotech/redo-model/third-party";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  IconButton,
} from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import EditPencil from "@redotech/redo-web/icon-old/edit-pencil.svg";
import Info from "@redotech/redo-web/icon-old/info.svg";
import LeftArrowMedium from "@redotech/redo-web/icon-old/left-arrow-medium.svg";
import ModalCloseButton from "@redotech/redo-web/icon-old/modal-close-button.svg";
import { InputSize } from "@redotech/redo-web/input";
import { TextInput } from "@redotech/redo-web/text-input";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import * as classNames from "classnames";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as chooseItems from "../choose-items.module.css";
import { EwUpsellStep } from "./ew-upsell-step";
import { BlockedStep, MultipleChoiceStep, RejectStep } from "./flow-steps";
import { InformationStep } from "./information-step";
import { InputStep } from "./input-step";
import { Product } from "./product";
import { ProductData } from "./product-data";
import * as productCss from "./product.module.css";
import { ReturnStep } from "./return-step";
import * as questions from "./return.module.css";
import { SubmitClaim } from "./submit-claim";
import { SubmitRepair } from "./submit-repair";
import { SubmitWarranty } from "./submit-warranty";
import { SubmitWarrantyRegistration } from "./submit-warranty-registration";
import { ChooseVariantFooter, VariantExchange } from "./variant-exchange";

const finalReturnSteps = [Step.REJECT, Step.BLOCK];

const SelectVariantfooter: ChooseVariantFooter = memo(
  function SelectVariantfooter({ enabled, onSubmit, pending }) {
    return (
      <Button
        className={questions.optionButton}
        disabled={!enabled}
        onClick={onSubmit}
        pending={pending}
        size={ButtonSize.MEDIUM}
        theme={ButtonTheme.PRIMARY}
      >
        Select type
      </Button>
    );
  },
);

export const ReturnItemFlow = memo(function ReturnItemFlow({
  draftReturn,
  product,
  returnableItem,
  onCancel,
  onSubmit,
  onEdit,
  onReject,
  variantSelected,
  setVariantSelected,
  settings,
  selectedReturnItem,
  isWarrantyRegistration,
}: {
  draftReturn: DraftReturn;
  product: ReturnProduct;
  returnableItem: CombinedReturnItem;
  onCancel: () => void;
  onSubmit: (item: NewReturnItemData) => void;
  onReject?: () => void;
  variantSelected: boolean | null;
  setVariantSelected: (variantSelected: boolean) => void;
  onEdit: (draftReturn: DraftReturn, newItemId: string) => void;
  settings: ReturnAppSettings;
  selectedReturnItem: ReturnableItem;
  isWarrantyRegistration?: boolean;
}) {
  const flow = useMemo(
    () =>
      returnFlowJsonFormat.read(
        product.reset?.returnFlow &&
          draftReturn.flow?.type === product.reset.returnFlow?.type
          ? {
              steps: JSON.parse(JSON.stringify(product.reset.returnFlow.steps)),
            }
          : draftReturn.flow,
      ),
    [draftReturn.flow, product.reset?.returnFlow],
  );

  const client = useRequiredContext(RpcClientContext);
  const order = draftReturn.orders.find(
    (order) => order.id === returnableItem.orderId,
  );

  const copyResource = getResource({
    variant: draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  const [editItemLoad, doEditItem] = useTriggerLoad(async (signal) => {
    const request = { draftReturnId: draftReturn._id, item: returnableItem };
    const response = await client.editReturnableItem(request, { signal });
    onEdit(response.draftReturn, response.newItemId);
    setVariantSelected(true);
  });

  const rejectItemIdRef = useRef<{
    rejectReason: string;
    rejectMessage: string;
    returnableItemId: string;
    flowPath: readonly number[];
    multipleChoiceAnswers: readonly { step: number; answer: number }[];
  } | null>(null);
  const [_rejectItemLoad, rejectItem] = useTriggerLoad(async (signal) => {
    if (rejectItemIdRef.current === null) {
      return;
    }

    const request = {
      draftReturnId: draftReturn._id,
      ...rejectItemIdRef.current,
      returnableItemIds: [rejectItemIdRef.current.returnableItemId],
      channel: Channel.WEB,
    };
    await client.rejectReturnItems(request, { signal });
  });

  const getNextFlowStep = useHandler((start: number): readonly number[] => {
    const { path, rejected } = walkFlow({
      draftReturn,
      flow,
      order,
      product,
      returnableItem,
      start,
    });

    if (rejected) {
      rejectItemIdRef.current = {
        ...rejected,
        flowPath: path,
        multipleChoiceAnswers: [],
      };
      rejectItem();
    }
    return path;
  });
  const [flowSteps, setFlowSteps] = useState<readonly ItemReturnFlowStep[]>(
    () => [
      {
        type: StepType.FLOW,
        flowPath: getNextFlowStep(
          product.reset?.returnFlow?.type === draftReturn.flow?.type &&
            product.reset?.step
            ? product.reset?.step
            : 0,
        ),
        inputAnswers: [],
        multipleChoiceAnswers: [],
      },
    ],
  );

  // Show the user the selected variant during variant exchange
  const [visibleVariant, onVariantVisible] = useState<ExchangeVariant>();

  const step = flowSteps[flowSteps.length - 1];
  const flowStepIndex = step.flowPath[step.flowPath.length - 1];
  const flowStep = flow.steps[flowStepIndex];

  const canGoBack = useMemo(() => {
    if (flowSteps.length <= 1) {
      return false;
    }
    return !finalReturnSteps.includes(flowStep.type);
  }, [flowSteps.length, flowStep]);

  const handleBack = useHandler(() => {
    if (flowSteps.length === 1) {
      return;
    }
    setFlowSteps((prev) => prev.slice(0, prev.length - 1));
  });

  const selectMultipleChoice = useHandler(
    (option: MultipleChoiceOption, index: number) => {
      const nextPath = getNextFlowStep(option.next);
      const nextStep = flow.steps[nextPath[nextPath.length - 1]];
      if (nextStep.type === Step.REJECT) {
        rejectItemIdRef.current = {
          returnableItemId: returnableItem.id,
          rejectMessage: nextStep.message,
          rejectReason: option.name,
          flowPath: step.flowPath,
          multipleChoiceAnswers: [
            ...step.multipleChoiceAnswers,
            { step: flowStepIndex, answer: index },
          ],
        };
        rejectItem();
      }
      setFlowSteps([
        ...flowSteps,
        {
          type: StepType.FLOW,
          flowPath: [...step.flowPath, ...nextPath],
          inputAnswers: step.inputAnswers,
          multipleChoiceAnswers: [
            ...step.multipleChoiceAnswers,
            { step: flowStepIndex, answer: index, fieldId: option.fieldId },
          ],
        },
      ]);
    },
  );

  const informationClickedNext = useHandler(() => {
    if (flowStep.type !== Step.INFORMATION) {
      throw new Error("Invalid step type");
    }
    const nextPath = getNextFlowStep(flowStep.next!);
    setFlowSteps([
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: [...step.flowPath, ...nextPath],
        inputAnswers: [...step.inputAnswers],
        multipleChoiceAnswers: step.multipleChoiceAnswers,
      },
    ]);
  });

  const submitInput = useHandler((answerParts: InputAnswerPart[]) => {
    if (flowStep.type !== Step.INPUT) {
      throw new Error("Invalid step type");
    }
    const nextPath = getNextFlowStep(flowStep.next!);
    const inputAnswerPartWithStep: InputAnswerPartWithStep[] = answerParts.map(
      (answerPart) => ({ ...answerPart, step: flowStepIndex }),
    );
    setFlowSteps([
      ...flowSteps,
      {
        type: StepType.FLOW,
        flowPath: [...step.flowPath, ...nextPath],
        inputAnswers: [...step.inputAnswers, ...inputAnswerPartWithStep],
        multipleChoiceAnswers: step.multipleChoiceAnswers,
      },
    ]);
  });

  const submitEWUpsell = useHandler((selectedOffering?: OfferingSelection) => {
    if (flowStep.type !== Step.EW_UPSELL) {
      throw new Error("Invalid step type");
    }
    const nextPath = getNextFlowStep(flowStep.next!);
    const ewInputAnswer: InputAnswerPartWithStep = {
      message: "Expanded Warranty Selection",
      type: InputPartType.JSON,
      value: JSON.stringify(selectedOffering),
      fieldId: FieldId.WARRANTIES_PATH_EW_UPSELL,
      step: flowStepIndex,
    };
    setFlowSteps([
      ...flowSteps,
      {
        type: StepType.FLOW,
        inputAnswers: [...step.inputAnswers, ewInputAnswer],
        multipleChoiceAnswers: step.multipleChoiceAnswers,
        flowPath: [...step.flowPath, ...nextPath],
      },
    ]);
  });

  const navigate = useNavigate();
  const onModalCloseAction = useCallback(
    (flowStep: Step) => {
      if (flowStep.type === Step.REJECT) {
        return onReject ?? (() => navigate("../"));
      }
      return onCancel;
    },
    [onReject, onCancel, navigate],
  );

  const submitItem = useHandler((data: CompensationMethodData) => {
    onSubmit({
      ...data,
      item: returnableItem,
      flow,
      flowPath: step.flowPath,
      inputAnswers: step.inputAnswers,
      multipleChoiceAnswers: step.multipleChoiceAnswers,
    });
  });

  let component: React.ReactNode;

  const sameItemVariant = product.variantData.variants.find(
    (variant) =>
      String(variant.id) === product.variantId &&
      isVariantInStock(variant, settings),
  );

  const chooseVariant = (variant: ExchangeVariant) => {
    product.variantId = variant.id;
    product.variantTitle = variant.title;
    product.price = variant.price;
    product.returnValue = variant.price;
    product.image = variant.image || product.image;
    if (!isWarrantyRegistration) {
      doEditItem();
    } else {
      setVariantSelected(true);
    }
  };

  switch (flowStep.type) {
    case Step.REJECT: {
      component = (
        <RejectStep
          complete={onReject ?? (() => navigate("../"))}
          step={flowStep}
        />
      );
      break;
    }
    case Step.BLOCK:
      component = <BlockedStep complete={onCancel} step={flowStep} />;
      break;
    case Step.INPUT:
      component = <InputStep step={flowStep} submit={submitInput} />;
      break;
    case Step.INFORMATION: {
      component = (
        <InformationStep nextClicked={informationClickedNext} step={flowStep} />
      );
      break;
    }
    case Step.MULTIPLE_CHOICE:
      component = (
        <MultipleChoiceStep
          draftReturnId={draftReturn._id}
          selectOption={selectMultipleChoice}
          step={flowStep}
          stepIndex={flowStepIndex}
        />
      );
      break;
    case Step.EW_UPSELL:
      component = (
        <EwUpsellStep
          collectionSettings={settings.extendedWarranties?.collectionSettings}
          product={product}
          step={flowStep}
          submit={submitEWUpsell}
          text={widgetConfigToText(settings.extendedWarranties)}
        />
      );
      break;
    case Step.RETURN: {
      component = (
        <ReturnStep
          flow={flow}
          hasStorefrontAccess={settings.hasStorefrontAccess}
          onBack={handleBack}
          onVariantVisible={onVariantVisible}
          product={product}
          pushReturnMethodStep={(nextStep) => {
            setFlowSteps((prev) => [...prev, nextStep]);
          }}
          returnStep={flowStep}
          selectedReturnItem={selectedReturnItem}
          settings={settings}
          step={step}
          submitItem={submitItem}
        />
      );
      break;
    }
    case Step.SUBMIT_CLAIM:
      component = (
        <SubmitClaim
          flowStep={flowStep}
          returnType={draftReturn.returnType}
          sameItemVariant={sameItemVariant as ExchangeVariant}
          showEnsoEngraving={isEnsoEngraving(
            settings,
            product,
            draftReturn.returnType,
          )}
          submitItem={submitItem}
        />
      );
      break;
    case Step.SUBMIT_REPAIR:
      component = <SubmitRepair flowStep={flowStep} submitItem={submitItem} />;
      break;
    case Step.SUBMIT_WARRANTY:
      component = (
        <SubmitWarranty
          flowStep={flowStep}
          sameItemVariant={sameItemVariant as ExchangeVariant}
          submitItem={submitItem}
        />
      );
      break;
    case Step.SUBMIT_WARRANTY_REGISTRATION:
      component = (
        <SubmitWarrantyRegistration
          draftReturn={draftReturn}
          item={{
            item: returnableItem,
            flow,
            flowPath: step.flowPath,
            inputAnswers: step.inputAnswers,
            multipleChoiceAnswers: step.multipleChoiceAnswers,
          }}
          submit={submitItem}
        />
      );
      break;
    default:
      throw new Error(`Invalid return flow step type: ${step.type}`);
  }

  if (!variantSelected && product.variantData.variants.length > 1) {
    component = (
      <VariantExchange
        chooseVariant={chooseVariant}
        footer={SelectVariantfooter}
        headerText={copyResource.CHOOSE_VARIANT_TEXT}
        ignoreStock
        multipleChoiceAnswers={getMultipleChoiceAnswers(
          step.multipleChoiceAnswers,
          flow,
          step.flowPath,
        ).map((answer) => answer.answer)}
        onBack={handleBack}
        onVariantVisible={onVariantVisible}
        product={product}
        returnType={draftReturn.returnType}
        settings={settings}
        submitItem={submitItem}
        submitPending={editItemLoad.pending}
      />
    );
  }

  return (
    <ReturnItemModal
      expandedProductsLength={selectedReturnItem.products.length - 1}
      handleBack={canGoBack ? handleBack : null}
      hideProduct={
        step?.type === "exchange-group-selected" || step?.type === "bundle-type"
      }
      isExpanded={selectedReturnItem.isExpanded}
      onCancel={onModalCloseAction(flowStep)}
      product={product}
      variant={visibleVariant}
    >
      {component}
    </ReturnItemModal>
  );
});

const ReturnItemModal = memo(function ReturnItemModal({
  product,
  onCancel,
  handleBack,
  children,
  variant,
  hideProduct = false,
  isExpanded = false,
  expandedProductsLength = 0,
}: {
  product: ReturnProduct;
  onCancel: () => void;
  handleBack: (() => void) | null;
  children: React.ReactNode;
  hideProduct?: boolean;
  variant?: ExchangeVariant;
  isExpanded?: boolean;
  expandedProductsLength?: number;
}) {
  return (
    <Modal
      onClose={onCancel}
      open
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div className={chooseItems.cardWrapper}>
        <div className={chooseItems.card}>
          <div className={productCss.modalButtons}>
            {/* {savedSteps.length > 0 && !hideBackButton ? ( */}
            {handleBack ? (
              <LeftArrowMedium
                className={productCss.closeButton}
                onClick={handleBack}
              />
            ) : (
              <div />
            )}
            <ModalCloseButton
              className={productCss.closeButton}
              onClick={onCancel}
            />
          </div>
          <div className={productCss.cardContent}>
            {!hideProduct && (
              <ProductData
                expandedProductsLength={expandedProductsLength}
                isExpanded={isExpanded}
                product={product}
                variant={variant}
              />
            )}
            {/* <Flex dir="column" grow={1} className={questions.contentWrapper}> */}
            {children}
            {/* </Flex> */}
          </div>
        </div>
      </div>
    </Modal>
  );
});

export const ReturnItemCard = memo(function ReturnItemCard({
  draftReturn,
  setDraftReturn,
  returnableItem,
  selectedItem,
  setSelectedItem,
  setVariantSelected,
  addItemLoad,
  addItemRef,
  copyResource,
  settings,
  client,
}: {
  draftReturn: DraftReturn;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  returnableItem: CombinedReturnItem;
  selectedItem: CombinedReturnItem | null;
  setSelectedItem: (item: CombinedReturnItem) => void;
  setVariantSelected: (variantSelected: boolean) => void;
  addItemLoad: LoadState<void | undefined>;
  addItemRef: React.MutableRefObject<NewReturnItemData | null>;
  copyResource: Resource;
  settings: ReturnAppSettings;
  client: CustomerPortalRpcClient;
}) {
  const primaryProduct = getPrimaryProduct(returnableItem);
  const otherProducts = getNonPrimaryProducts(returnableItem);

  const rejectItemIdRef = useRef<{
    rejectReason: string;
    rejectMessage: string;
    returnableItemId: string;
  } | null>(null);

  const order = draftReturn.orders.find(
    (order) => order.id === returnableItem.orderId,
  );
  const disabledMessage = useMemo(() => {
    if (!settings.returns?.disableRejectedItems || !primaryProduct) {
      return null;
    }
    const flow = returnFlowJsonFormat.read(
      primaryProduct.reset?.returnFlow &&
        draftReturn.flow?.type === primaryProduct.reset.returnFlow?.type
        ? {
            steps: JSON.parse(
              JSON.stringify(primaryProduct.reset.returnFlow.steps),
            ),
          }
        : draftReturn.flow,
    );

    const { step, rejected } = walkFlow({
      draftReturn,
      flow,
      order,
      product: primaryProduct,
      returnableItem,
      start: 0,
    });

    if (rejected) {
      rejectItemIdRef.current = rejected;
    }

    switch (step.type) {
      case Step.REJECT:
      case Step.BLOCK:
        return step.message;
      default:
        return null;
    }
  }, [
    draftReturn,
    order,
    primaryProduct,
    returnableItem,
    settings.returns?.disableRejectedItems,
  ]);

  if (!primaryProduct) {
    return null;
  }

  return (
    <div className={productCss.container}>
      <div
        className={classNames(productCss.productCard, {
          // [product.failed]: disabled,
          // [product.disabled]: freStatus && disabled,
        })}
      >
        <div>
          <Product
            key={getKey(primaryProduct)}
            returnProduct={primaryProduct}
          />
          {otherProducts.map((product) => (
            <Product
              expandedButNotUnbundled={
                returnableItem.isExpanded && !product.isUnbundled
              }
              key={getKey(product)}
              returnProduct={product}
            />
          ))}
        </div>
        {returnableItem.pendingItem && (
          <div
            className={classNames(
              productCss.tagContainer,
              productCss.infoContainer,
            )}
          >
            <div className={productCss.tag}>
              {copyResource.PENDING_ITEM_PILL_TEXT}
            </div>
          </div>
        )}
        <Flex dir="column" flexDirection="column" justify="center">
          <div className={productCss.selectButtonContainer}>
            <div
              className={classNames(
                productCss.infoContainer,
                productCss.rejectedTooltip,
              )}
            >
              {disabledMessage ? (
                <Tooltip
                  enterTouchDelay={0}
                  placement="top"
                  title={disabledMessage}
                >
                  <IconButton>
                    <Info className={productCss.infoIcon} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </div>
            <Button
              className={productCss.selectButton}
              disabled={!!disabledMessage}
              onClick={() => {
                setVariantSelected(order?.salesChannel !== THIRD_PARTY_SOURCE);
                setSelectedItem(returnableItem);
              }}
              pending={
                returnableItem === selectedItem ||
                (addItemLoad.pending &&
                  addItemRef.current?.item === returnableItem)
              }
              size={ButtonSize.SMALL}
              theme={ButtonTheme.OUTLINED}
            >
              {returnableItem.pendingItem ? (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <EditPencil />
                  <div>Change</div>
                </div>
              ) : (
                "Select"
              )}
            </Button>
          </div>
          <Flex justify="center">
            {returnableItem.availableQuantity > 0 &&
              !returnableItem?.pendingItem &&
              settings?.bulkReturnsEnabled && (
                <div>
                  {`Quantity available: ${returnableItem.availableQuantity}`}
                </div>
              )}
            {returnableItem.pendingItem?.quantity &&
              returnableItem.pendingItem.quantity > 0 &&
              settings?.bulkReturnsEnabled && (
                <EditableReturnQuantity
                  client={client}
                  draftReturnId={draftReturn._id}
                  maxAvailableQuantity={
                    returnableItem.availableQuantity +
                    (returnableItem.pendingItem?.quantity ?? 1)
                  }
                  returnableItem={returnableItem}
                  setDraftReturn={setDraftReturn}
                />
              )}
          </Flex>
        </Flex>
      </div>
    </div>
  );
});

export const EditableReturnQuantity = memo(function ReturnItemCard({
  returnableItem,
  maxAvailableQuantity,
  client,
  draftReturnId,
  setDraftReturn,
}: {
  returnableItem: CombinedReturnItem;
  maxAvailableQuantity: number;
  client: CustomerPortalRpcClient;
  draftReturnId: string;
  setDraftReturn: (draftReturn: DraftReturn) => void;
}) {
  const [quantity, setQuantity] = useState<string>(
    String(returnableItem.pendingItem?.quantity ?? 1),
  );
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [editQuantityLoad, doEditQuantity] = useTriggerLoad(async (signal) => {
    if (!returnableItem.pendingItem) {
      return;
    }

    const request = {
      draftReturnId: draftReturnId,
      pendingItemId: returnableItem.pendingItem.id,
      updatedQuantity: Number(quantity),
    };
    const response = await client.editPendingItemQuantity(request, { signal });
    if (response.draftReturn) {
      setDraftReturn(response.draftReturn);
    }
    setIsFocused(false);
  });

  return (
    <Flex display="flex">
      <Flex
        className={
          isFocused
            ? chooseItems.returnQuantityTextContainerFocused
            : chooseItems.returnQuantityTextContainer
        }
        display="flex"
        m="auto"
      >
        {`Return quantity: `}
      </Flex>
      {isFocused ? (
        <>
          <div className={chooseItems.returnQuantityInputContainer}>
            <TextInput
              allowInvalidNumbers
              max={maxAvailableQuantity}
              min={0}
              onChange={(val: string) => setQuantity(val)}
              size={InputSize.MEDIUM}
              type="number"
              value={quantity}
            />
          </div>
          <Button
            onClick={() => {
              doEditQuantity();
            }}
            pending={editQuantityLoad.pending}
            theme={ButtonTheme.PRIMARY}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Flex display="flex" m="auto">
            {returnableItem?.pendingItem?.quantity ?? 1}
          </Flex>
          <Flex m="auto">
            <IconButton
              onClick={() => {
                setIsFocused(true);
              }}
            >
              <EditPencil />
            </IconButton>
          </Flex>
        </>
      )}
    </Flex>
  );
});
